import { useEffect, useState } from 'react';
import { FileWarning, Loader2, ExternalLink } from 'lucide-react';
import { qualityAPI, Concession } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { useColumnSort } from '../../hooks/useColumnSort';
import { SortableTh } from '../common/SortableTh';

interface POLineConcessionTabProps {
  lineId: number;
}

/** Mirrors the concession list's own status colouring. */
const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-amber-100 text-amber-800',
  under_review: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  closed: 'bg-gray-100 text-gray-600',
};

const IN_FLIGHT = ['submitted', 'under_review'];

/**
 * CR-7263: sort keys for the columns that render a display label rather than
 * the stored value. Type and Status both fall back through `*_display`, and
 * Raised By falls back to the supplier's name -- so each derives its key the
 * same way its cell derives its text. Module scope: the hook memoises on this
 * object's identity.
 */
const CONCESSION_SORT_KEYS = {
  request_type: (c: Concession) =>
    c.request_type_display || c.deviation_type_display || c.deviation_type,
  status: (c: Concession) => c.status_display || c.status,
  requested_by_name: (c: Concession) => c.requested_by_name || c.supplier_name,
} as const;

/**
 * CR-6510: the concessions raised against one PO line.
 *
 * The order screen carried a "Concession under review" badge and no way to
 * act on it — the badge was informational, and the concession list could not
 * be searched by PO number, so a buyer had to already know the part number to
 * find the record. This is the drill-down the ticket's indicator implies, and
 * the one `po_line` filter on the concessions endpoint was added for.
 *
 * Sits alongside the NCR and GRN tabs and follows their shape deliberately:
 * a buyer looking for related records on a line should not have to learn a
 * different pattern per record type. Links open in a new tab for the same
 * reason they do on the NCR tab — navigating in place would close the modal
 * and lose the buyer's position in the order.
 */
export default function POLineConcessionTab({ lineId }: POLineConcessionTabProps) {
  const [concessions, setConcessions] = useState<Concession[]>([]);
  // CR-7263: one line's concessions, fully loaded and unpaginated, so sorting
  // the array is the whole answer. Declared above the loading/error early
  // returns -- a hook below one of those is called on some renders and not
  // others.
  const { sortedRows: sortedConcessions, sortField, sortDirection, onSort } = useColumnSort(
    concessions,
    {
      numericFields: ['quantity_affected'],
      dateFields: ['requested_date'],
      computed: CONCESSION_SORT_KEYS,
    },
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { formatDate: contextFormatDate } = useFormatters();
  const formatDate = (value?: string) => (value ? contextFormatDate(value) : 'N/A');

  useEffect(() => {
    fetchConcessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineId]);

  const fetchConcessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await qualityAPI.getConcessions({
        po_line: lineId, page_size: 50,
      });
      setConcessions(response.data.results || []);
    } catch (err: unknown) {
      console.error('Failed to fetch concessions:', err);
      setError(getApiErrorMessage(err, 'Failed to load concessions'));
      toast.error('Failed to load concessions');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button
          onClick={fetchConcessions}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (concessions.length === 0) {
    return (
      <div className="text-center py-12">
        <FileWarning className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">No concessions raised against this PO line</p>
      </div>
    );
  }

  const awaitingDecision = concessions.filter(c => IN_FLIGHT.includes(c.status));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="text-sm text-muted-foreground">
          {concessions.length} concession{concessions.length !== 1 ? 's' : ''} found
          {awaitingDecision.length > 0
            && ` · ${awaitingDecision.length} awaiting a decision`}
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 border-b">
              <SortableTh field="concession_number" sortField={sortField} sortDirection={sortDirection} onSort={onSort} className="px-4 py-3 font-medium">Number</SortableTh>
              <SortableTh field="requested_date" sortField={sortField} sortDirection={sortDirection} onSort={onSort} className="px-4 py-3 font-medium">Raised</SortableTh>
              <SortableTh field="request_type" sortField={sortField} sortDirection={sortDirection} onSort={onSort} className="px-4 py-3 font-medium">Type</SortableTh>
              <SortableTh field="quantity_affected" sortField={sortField} sortDirection={sortDirection} onSort={onSort} className="px-4 py-3 font-medium">Qty Affected</SortableTh>
              <SortableTh field="status" sortField={sortField} sortDirection={sortDirection} onSort={onSort} className="px-4 py-3 font-medium">Status</SortableTh>
              <SortableTh field="requested_by_name" sortField={sortField} sortDirection={sortDirection} onSort={onSort} className="px-4 py-3 font-medium">Raised By</SortableTh>
              {/* Not sortable: the row's link, which is the same on every row. */}
              <th className="text-left px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {sortedConcessions.map((concession) => (
              <tr key={concession.id} className="border-b last:border-b-0 hover:bg-muted/30">
                <td className="px-4 py-3 font-medium">
                  <a
                    href={`/quality/concession/${concession.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {concession.concession_number}
                  </a>
                </td>
                <td className="px-4 py-3">{formatDate(concession.requested_date)}</td>
                <td className="px-4 py-3">
                  {concession.request_type_display
                    || concession.deviation_type_display
                    || concession.deviation_type}
                </td>
                <td className="px-4 py-3">{concession.quantity_affected}</td>
                <td className="px-4 py-3">
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-semibold ${
                      STATUS_STYLES[concession.status] || 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {concession.status_display || concession.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {concession.requested_by_name || concession.supplier_name}
                </td>
                <td className="px-4 py-3">
                  <a
                    href={`/quality/concession/${concession.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1 whitespace-nowrap"
                  >
                    {/* Names the destination rather than saying "View", because
                        for an outstanding request that page is where the
                        customer approves or rejects it. */}
                    {IN_FLIGHT.includes(concession.status) ? 'Review' : 'Open'}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
